import type { ICorePageContextMappings, InitialTrackingDimensions } from '@/services/Analytics/bnefAnalyticsInterfaces';
import { PLATFORM_CUSTOM_DIMENSION } from '@bbnpm/bb-analytics';

/* Matomo/BB-Analytics Custom Dimension
BNEF adopted BB-Analytics early on and have been using Matomo Custom Dimension ID 1-10
    https://matomo.prod.bloomberg.com/index.php?module=CustomDimensions&action=manage&idSite=30
Later on, BB-Analytics team reserved ID 1-10 and 51-65 so that the value is the same for all sites company-wide
    https://bbgithub.dev.bloomberg.com/bbnpm/bb-analytics/blob/6.1.0/src/providers/bloomberg-provider.constants.ts#L60
    These conflict with our usage of ID 1-10.
Our Matomo site still have old mapping, but bb-analytics JS library no longer allow us to write to reserved dimension,
we need to map our dimension name to BB-Analytics dimension name of the same ID
    For example Dimension ID 2; we use it for "userSiteLanguage", they use for "hostname"
    We call `bb-analytics.logPageVisit({hostname: 'en'});`
    bb-analytics map 'hostname' to 'dimension2' and send out tracking event {event: 'pageVisit', dimension2: 'en'}
    Matomo store 'en' value in Dimension ID 2, which, for our site, is displayed as 'User Locale'
*/
const mapMatomoConflictedDimensionsToBnef: Partial<
    Record<(typeof PLATFORM_CUSTOM_DIMENSION)[keyof typeof PLATFORM_CUSTOM_DIMENSION], keyof ICorePageContextMappings>
> = {
    [PLATFORM_CUSTOM_DIMENSION.USER_TYPE]: 'userHasInterests',
    [PLATFORM_CUSTOM_DIMENSION.HOSTNAME]: 'userSiteLanguage',
    [PLATFORM_CUSTOM_DIMENSION.DIMENSION3]: 'userType',
    [PLATFORM_CUSTOM_DIMENSION.DIMENSION4]: 'contentId',
    [PLATFORM_CUSTOM_DIMENSION.DIMENSION5]: 'bnefPreviousPageName',
    [PLATFORM_CUSTOM_DIMENSION.DIMENSION6]: 'bnefPageName',
    [PLATFORM_CUSTOM_DIMENSION.DIMENSION7]: 'siteSection',
    [PLATFORM_CUSTOM_DIMENSION.DIMENSION8]: 'externalReferrerSource',
    [PLATFORM_CUSTOM_DIMENSION.DIMENSION9]: 'audience',
    [PLATFORM_CUSTOM_DIMENSION.DIMENSION10]: 'colorScheme',
};
// Other of our custom dimension that doesn't conflict
export const MATOMO_USER_CUSTOM_DIMENSION_MAP: { [K in keyof ICorePageContextMappings]: number } = {
    // in case we start using other unreserved dimensions
    // userPreferReducedMotion: 11, // Example
};

const validMatomoDimensionNames = Object.keys({
    ...mapMatomoConflictedDimensionsToBnef,
    ...MATOMO_USER_CUSTOM_DIMENSION_MAP,
});
export const mapBnefTrackingDimensionToMatomo = (values: InitialTrackingDimensions) =>
    Object.fromEntries(
        validMatomoDimensionNames.map((key) =>
            // @ts-expect-error key doesn't exist in type, but the values are null-coalesced
            [key, values[mapMatomoConflictedDimensionsToBnef[key] ?? key] ?? null]
        )
    );
